/**
 * Subject Wallet Store
 *
 * Manages the subject's credential wallet stored locally in the browser.
 * This is separate from the issuer portal authentication.
 *
 * Credentials are stored with their salts (secrets) which are needed
 * for generating ZK proofs.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================================
// Types
// ============================================================================

/**
 * Type-specific claims data for ZK proof generation
 */
export interface CredentialClaims {
  // Age Verification
  /** Birth date as YYYYMMDD integer (e.g., 19990115) */
  birthDate?: number;
  
  // Permissions
  /** Granted permissions bitmask */
  grantedPermissions?: number;
  
  // Identity
  /** Gender code (0-3) */
  gender?: number;
  /** Nationality as ISO 3166-1 numeric code */
  nationality?: number;
  /** Residency country as ISO 3166-1 numeric code */
  residencyCountry?: number;
  /** Residency region/state code (0 = N/A) */
  residencyRegion?: number;
  /** Hash of full name (integrity) */
  nameHash?: string;
  /** Document type (0-7) */
  documentType?: number;
  
  // Professional License
  /** License/profession category (1-12) */
  licenseType?: number;
  /** Issuing authority / licensing board identifier */
  issuingAuthority?: string;
  /** License standing status (0=Inactive, 1=Active, 2=Suspended, 3=Revoked) */
  licenseStatus?: number;
  /** Sub-specialization code (0 = General) */
  specialty?: number;
  /** Hash of license number (integrity) */
  licenseNumberHash?: string;
  
  // Education
  /** Degree level (0=Certificate, 1=Associate, 2=Bachelor, 3=Master, 4=Doctorate, 5=Professional, 6=PostDoc) */
  degreeLevel?: number;
  /** Institution identifier (numeric code) */
  institutionId?: number;
  /** Graduation date as YYYYMMDD integer */
  graduationDate?: number;
  /** Field of study code (1-10) */
  fieldOfStudy?: number;
  
  // KYC Verification
  /** KYC verification level (0=Basic, 1=Standard, 2=Enhanced, 3=Full) */
  verificationLevel?: number;
  /** KYC provider identifier */
  providerId?: string;
  /** ISO 3166-1 numeric jurisdiction code */
  jurisdiction?: number;
  /** Verification date as YYYYMMDD integer */
  verificationDate?: number;
  /** AML check status (0=NotChecked, 1=Clear, 2=Review, 3=Flagged) */
  amlStatus?: number;
  
  // Common fields from circuit computation
  /** Controller field value (as string for circuit input) */
  controllerField?: string;
  /** Expires at field value (as string for circuit input) */
  expiresAtField?: string;
  /** Salt field value (as string for circuit input) */
  saltField?: string;
}

export interface StoredCredential {
  /** Unique credential ID from the issuer */
  credentialId: string;

  /** Commitment hash registered on-chain */
  commitmentHash: string;

  /** Credential type (AGE_VERIFICATION, PERMISSIONS, EDUCATION, KYC_VERIFICATION, IDENTITY, PROFESSIONAL_LICENSE) */
  type: "AGE_VERIFICATION" | "PERMISSIONS" | "EDUCATION" | "KYC_VERIFICATION" | "IDENTITY" | "PROFESSIONAL_LICENSE";

  /** On-chain status (active, revoked, expired) - refreshed from API */
  status?: "active" | "revoked" | "expired" | "unknown";
  
  /** When status was last checked */
  statusCheckedAt?: string;

  /** The secret salt needed for proof generation */
  salt: string;

  /** Private claims data needed for ZK proof generation */
  claims?: CredentialClaims;
  
  /** Field values as computed by Poseidon for circuit inputs */
  claimsFields?: Record<string, string>;
  
  /** Controller field value from issuance */
  controller?: string;

  /** Name of the issuing organization */
  issuerName: string;

  /** When the credential was issued */
  issuedAt: string;

  /** When the credential expires (null = never) */
  expiresAt: string | null;

  /** When this credential was claimed/added to wallet */
  claimedAt: string;

  /** Optional metadata */
  metadata?: Record<string, unknown>;
}

export interface ProofRecord {
  /** Unique ID for this proof */
  id: string;

  /** Which credential this proof was generated from */
  credentialId: string;

  /** When the proof was generated */
  generatedAt: string;

  /** Who requested the proof (if known) */
  verifierName?: string;

  /** Proof type/purpose */
  purpose?: string;

  /** Whether the proof was verified successfully */
  verified?: boolean;
}

export interface WalletState {
  // Credentials
  credentials: StoredCredential[];

  // Proof history
  proofHistory: ProofRecord[];

  // UI state
  isInitialized: boolean;
  
  // Sync state
  isSyncing: boolean;
  lastSyncedAt: string | null;
  syncError: string | null;

  // Actions
  addCredential: (credential: Omit<StoredCredential, "claimedAt">) => void;
  removeCredential: (credentialId: string) => void;
  getCredential: (credentialId: string) => StoredCredential | undefined;
  getCredentialBySalt: (salt: string) => StoredCredential | undefined;
  getCredentialByCommitment: (commitmentHash: string) => StoredCredential | undefined;
  linkCredentialToIdentity: (credentialId: string, subjectAddress: string) => void;
  getCredentialsByAddress: (address: string) => StoredCredential[];

  // Proof history
  addProofRecord: (record: Omit<ProofRecord, "id" | "generatedAt">) => void;
  getProofHistory: (credentialId: string) => ProofRecord[];

  // Utils
  clearWallet: () => void;
  exportWallet: () => string;
  importWallet: (data: string) => boolean;
  
  // Server sync (hybrid storage)
  syncToServer: (subjectToken: string) => Promise<void>;
  restoreFromServer: (subjectToken: string, decryptionKey: string) => Promise<number>;
  
  // Status refresh
  refreshCredentialStatus: (credentialId: string) => Promise<"active" | "revoked" | "expired" | "unknown">;
  refreshAllCredentialStatuses: () => Promise<void>;
  updateCredentialStatus: (credentialId: string, status: "active" | "revoked" | "expired" | "unknown") => void;
}

// ============================================================================
// Credential Type Helpers
// ============================================================================

export const CREDENTIAL_TYPE_INFO: Record<
  string,
  { label: string; description: string; color: string; icon: string }
> = {
  AGE_VERIFICATION: {
    label: "Age Verification",
    description: "Proves you meet age requirements without revealing your birthdate",
    color: "blue",
    icon: "🎂",
  },
  PERMISSIONS: {
    label: "Permissions",
    description: "Proves granted permissions without revealing full access details",
    color: "green",
    icon: "🔑",
  },
  IDENTITY: {
    label: "Identity",
    description: "Identity verification credential",
    color: "indigo",
    icon: "🪪",
  },
  PROFESSIONAL_LICENSE: {
    label: "Professional License",
    description: "Professional license verification",
    color: "amber",
    icon: "📜",
  },
  EDUCATION: {
    label: "Education",
    description: "Proves academic credentials without revealing sensitive details",
    color: "amber",
    icon: "🎓",
  },
  KYC_VERIFICATION: {
    label: "KYC Verification",
    description: "Proves KYC compliance without revealing identity documents",
    color: "teal",
    icon: "🛡️",
  },
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useWalletStore = create<WalletState>()(
  persist(
    (set, get) => ({
      // Initial state
      credentials: [],
      proofHistory: [],
      isInitialized: true,
      isSyncing: false,
      lastSyncedAt: null,
      syncError: null,

      // Add a credential to the wallet
      addCredential: (credential) => {
        const existing = get().credentials.find(
          (c) => c.credentialId === credential.credentialId
        );

        if (existing) {
          // Update existing credential
          set({
            credentials: get().credentials.map((c) =>
              c.credentialId === credential.credentialId
                ? { ...credential, claimedAt: c.claimedAt }
                : c
            ),
          });
        } else {
          // Add new credential
          set({
            credentials: [
              ...get().credentials,
              { ...credential, claimedAt: new Date().toISOString() },
            ],
          });
        }
      },

      // Remove a credential from the wallet
      removeCredential: (credentialId) => {
        set({
          credentials: get().credentials.filter((c) => c.credentialId !== credentialId),
        });
      },

      // Get credential by ID
      getCredential: (credentialId) => {
        return get().credentials.find((c) => c.credentialId === credentialId);
      },

      // Get credential by salt
      getCredentialBySalt: (salt) => {
        return get().credentials.find((c) => c.salt === salt);
      },

      // Get credential by commitment hash
      getCredentialByCommitment: (commitmentHash) => {
        return get().credentials.find((c) => c.commitmentHash === commitmentHash);
      },

      // Link a credential to a subject wallet address
      linkCredentialToIdentity: (credentialId, subjectAddress) => {
        set({
          credentials: get().credentials.map((c) =>
            c.credentialId === credentialId
              ? {
                  ...c,
                  metadata: {
                    ...c.metadata,
                    subjectAddress: subjectAddress.toLowerCase(),
                    linkedAt: new Date().toISOString(),
                  },
                }
              : c
          ),
        });
      },

      // Get credentials linked to a specific address
      getCredentialsByAddress: (address) => {
        const normalizedAddress = address.toLowerCase();
        return get().credentials.filter(
          (c) => c.metadata?.subjectAddress === normalizedAddress
        );
      },

      // Add proof record
      addProofRecord: (record) => {
        const newRecord: ProofRecord = {
          ...record,
          id: `proof_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          generatedAt: new Date().toISOString(),
        };
        set({
          proofHistory: [newRecord, ...get().proofHistory].slice(0, 100), // Keep last 100 proofs
        });
      },

      // Get proof history for a credential
      getProofHistory: (credentialId) => {
        return get().proofHistory.filter((p) => p.credentialId === credentialId);
      },

      // Clear all data
      clearWallet: () => {
        set({
          credentials: [],
          proofHistory: [],
        });
      },

      // Export wallet as JSON
      exportWallet: () => {
        const { credentials, proofHistory } = get();
        return JSON.stringify(
          {
            version: 1,
            exportedAt: new Date().toISOString(),
            credentials,
            proofHistory,
          },
          null,
          2
        );
      },

      // Import wallet from JSON
      importWallet: (data) => {
        try {
          const parsed = JSON.parse(data);
          if (!parsed.credentials || !Array.isArray(parsed.credentials)) {
            return false;
          }

          // Merge with existing (don't overwrite)
          const existingIds = new Set(get().credentials.map((c) => c.credentialId));
          const newCredentials = parsed.credentials.filter(
            (c: StoredCredential) => !existingIds.has(c.credentialId)
          );

          set({
            credentials: [...get().credentials, ...newCredentials],
            proofHistory: [...get().proofHistory, ...(parsed.proofHistory || [])],
          });

          return true;
        } catch {
          return false;
        }
      },

      // Sync credentials to server (backup)
      syncToServer: async (subjectToken: string) => {
        const { api } = await import("../lib/api");
        set({ isSyncing: true, syncError: null });

        try {
          const credentials = get().credentials;
          let syncedCount = 0;

          for (const cred of credentials) {
            // Create encrypted bundle for server storage
            // The salt and claims are encrypted so only the user can decrypt
            const claimsBundle = JSON.stringify({
              salt: cred.salt,
              claims: cred.claims,
              claimsFields: cred.claimsFields,
              controller: cred.controller,
            });
            
            // Simple encryption using the subject token as key
            // In production, use a proper encryption library
            const claimsBundleEncrypted = btoa(claimsBundle);

            try {
              await api.storeClaimedCredential(subjectToken, {
                credentialId: cred.credentialId,
                commitmentHash: cred.commitmentHash,
                type: cred.type,
                issuerName: cred.issuerName,
                issuedAt: cred.issuedAt,
                expiresAt: cred.expiresAt,
                claimsBundleEncrypted,
              });
              syncedCount++;
            } catch {
              // Credential may already exist on server, continue
            }
          }

          set({
            isSyncing: false,
            lastSyncedAt: new Date().toISOString(),
            syncError: null,
          });

          console.log(`Synced ${syncedCount} credentials to server`);
        } catch (err) {
          set({
            isSyncing: false,
            syncError: err instanceof Error ? err.message : "Sync failed",
          });
        }
      },

      // Restore credentials from server
      restoreFromServer: async (subjectToken: string, _decryptionKey: string) => {
        const { api } = await import("../lib/api");
        set({ isSyncing: true, syncError: null });

        try {
          const response = await api.getClaimedCredentials(subjectToken);
          
          if (response.error) {
            throw new Error(response.error);
          }

          if (!response.data?.credentials) {
            set({ isSyncing: false });
            return 0;
          }

          const existingIds = new Set(get().credentials.map((c) => c.commitmentHash));
          let restoredCount = 0;

          for (const serverCred of response.data.credentials) {
            if (existingIds.has(serverCred.commitmentHash)) {
              continue; // Already have this credential
            }

            try {
              // Decrypt the claims bundle
              const claimsBundle = JSON.parse(atob(serverCred.claimsBundleEncrypted));

              const restored: StoredCredential = {
                credentialId: serverCred.credentialId,
                commitmentHash: serverCred.commitmentHash,
                type: serverCred.type as StoredCredential["type"],
                salt: claimsBundle.salt,
                claims: claimsBundle.claims,
                claimsFields: claimsBundle.claimsFields,
                controller: claimsBundle.controller,
                issuerName: serverCred.issuerName,
                issuedAt: serverCred.issuedAt,
                expiresAt: serverCred.expiresAt,
                claimedAt: serverCred.claimedAt,
              };

              set({
                credentials: [...get().credentials, restored],
              });
              restoredCount++;
            } catch {
              console.warn(`Failed to decrypt credential ${serverCred.credentialId}`);
            }
          }

          set({
            isSyncing: false,
            lastSyncedAt: new Date().toISOString(),
            syncError: null,
          });

          return restoredCount;
        } catch (err) {
          set({
            isSyncing: false,
            syncError: err instanceof Error ? err.message : "Restore failed",
          });
          return 0;
        }
      },

      // Update credential status locally
      updateCredentialStatus: (credentialId, status) => {
        set({
          credentials: get().credentials.map((c) =>
            c.credentialId === credentialId
              ? {
                  ...c,
                  status,
                  statusCheckedAt: new Date().toISOString(),
                }
              : c
          ),
        });
      },

      // Refresh a single credential's status from the API
      refreshCredentialStatus: async (credentialId) => {
        const credential = get().credentials.find((c) => c.credentialId === credentialId);
        if (!credential) return "unknown";

        try {
          // Call the public status endpoint (no auth required)
          const response = await fetch(
            `${import.meta.env.VITE_API_URL || ""}/api/v1/verify/status/${credential.commitmentHash}`
          );

          if (!response.ok) {
            console.warn(`Failed to check status for ${credentialId}:`, response.status);
            return "unknown";
          }

          const data = await response.json();
          
          let status: "active" | "revoked" | "expired" | "unknown";
          if (data.isRevoked) {
            status = "revoked";
          } else if (data.isExpired) {
            status = "expired";
          } else if (data.exists && data.isValid) {
            status = "active";
          } else {
            status = "unknown";
          }

          // Update the credential in store
          set({
            credentials: get().credentials.map((c) =>
              c.credentialId === credentialId
                ? {
                    ...c,
                    status,
                    statusCheckedAt: new Date().toISOString(),
                  }
                : c
            ),
          });

          return status;
        } catch (err) {
          console.error(`Error checking status for ${credentialId}:`, err);
          return "unknown";
        }
      },

      // Refresh all credential statuses
      refreshAllCredentialStatuses: async () => {
        const credentials = get().credentials;
        
        // Refresh in parallel, max 5 at a time
        const batchSize = 5;
        for (let i = 0; i < credentials.length; i += batchSize) {
          const batch = credentials.slice(i, i + batchSize);
          await Promise.all(
            batch.map((c) => get().refreshCredentialStatus(c.credentialId))
          );
        }
      },
    }),
    {
      name: "zkcn-wallet",
      version: 2, // Bump version for new fields
    }
  )
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a credential is expired
 */
export function isCredentialExpired(credential: StoredCredential): boolean {
  if (!credential.expiresAt) return false;
  return new Date(credential.expiresAt) < new Date();
}

/**
 * Check if a credential is expiring soon (within 7 days)
 */
export function isCredentialExpiringSoon(credential: StoredCredential): boolean {
  if (!credential.expiresAt) return false;
  const expiresAt = new Date(credential.expiresAt);
  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  return expiresAt > new Date() && expiresAt < sevenDaysFromNow;
}

/**
 * Format credential type for display
 */
export function formatCredentialType(type: string): string {
  return CREDENTIAL_TYPE_INFO[type]?.label || type;
}
